import { CARRIER } from '../config/constants.js'
import { isOnFlightDeck } from './deckOutline.js'
import { DEG } from '../util/units.js'

/**
 * Where the rest of the air wing is parked.
 *
 * A carrier deck is never empty. Between recoveries there are twenty-odd
 * aeroplanes on it, and where they are is not decoration — it is the single
 * most important fact about a flight deck. Everything forward and outboard is
 * parking; the angled deck is the landing area and it has to be CLEAR. An
 * aeroplane, a tractor or a person inside that strip is a foul deck, and a
 * foul deck is a wave-off.
 *
 * So these are spots, not scenery. Each one is checked against the landing
 * area by `npm test`, because a parked Tomcat sitting on the centreline would
 * be a rendering detail that quietly contradicts the entire subject of the
 * trainer.
 *
 * Positions are in the SHIP frame — origin midships on the centreline, +X
 * forward, +Y starboard — because that is how a deck is laid out. The check
 * happens in the DECK frame, because that is where the landing area lives.
 */

/** @typedef {{x: number, y: number, headingDeg: number, name: string}} Spot */

/**
 * The spot list. Heading is relative to the ship's bow, positive to starboard,
 * which is how aircraft are actually chocked — nose out over the deck edge so
 * a tractor can pull them straight forward.
 *
 * @type {Spot[]}
 */
export const DECK_SPOTS = [
  // ---- the bow, forward of the catapults ---------------------------
  { x: 470, y: -46, headingDeg: 28, name: 'bow 1' },
  { x: 452, y: 22, headingDeg: 22, name: 'bow 2' },
  { x: 372, y: 50, headingDeg: 16, name: 'bow 3' },

  // ---- the street, alongside and abaft the island ------------------
  // Nose outboard over the deck edge: the packed-in parking that makes room on
  // a deck this width for aeroplanes with a 64 ft span.
  { x: 236, y: 60, headingDeg: 62, name: 'street forward' },
  { x: 120, y: 63, headingDeg: 68, name: 'street abeam' },
  { x: -30, y: 62, headingDeg: 74, name: 'street aft' },

  // ---- the point, on the port bow ----------------------------------
  //
  // Originally this was a six pack on the starboard QUARTER, and it could not
  // work. Going aft, the angled landing area swings toward the starboard side,
  // so by the stern anything clear of the wires is 110 ft out — thirty feet
  // beyond the deck edge. Three aeroplanes were hanging over open water while
  // passing a foul-line check that only ever asked about the wires.
  //
  // Forward of where the landing area ends, the port bow is wide open, which
  // is exactly why real carriers park there and call it the point.
  { x: 330, y: -92, headingDeg: -34, name: 'the point 1' },
  { x: 258, y: -100, headingDeg: -30, name: 'the point 2' },
  { x: 402, y: -78, headingDeg: -38, name: 'the point 3' },
]

/* ===================================================================== *
 *  THE FOUL LINE                                                        *
 * ===================================================================== */

/** Ship-frame point -> deck frame, feet. Pure, so it can be tested. */
export function shipToDeck(xFt, yFt) {
  // The angled deck is 9 degrees to port of the hull, with its origin at the
  // ramp. Rotating by the NEGATIVE of that angle takes a ship-frame point into
  // the deck's own axes.
  const th = -CARRIER.ANGLE_DEG * DEG
  const dx = xFt - CARRIER.RAMP_SHIP_X_FT
  const dy = yFt - CARRIER.RAMP_SHIP_Y_FT
  return {
    x: dx * Math.cos(th) + dy * Math.sin(th),
    y: -dx * Math.sin(th) + dy * Math.cos(th),
  }
}

/**
 * How far outside the landing area is this point, in feet?
 *
 * Negative means it is INSIDE — a foul deck. The margin is measured to the
 * edge of the landing area plus the span of a Tomcat's wing, because an
 * aeroplane parked with its wingtip over the foul line fouls the deck just as
 * surely as one parked in the middle of it.
 */
export function clearanceFt(xFt, yFt, halfSpanFt = 0) {
  const d = shipToDeck(xFt, yFt)
  const halfWidth = CARRIER.LANDING_AREA_WIDTH_FT / 2

  // Astern of the ramp or forward of the landing area: not in the strip at all.
  const aheadOfRamp = d.x > -20
  const withinLength = d.x < CARRIER.LANDING_AREA_LENGTH_FT + 20
  if (!aheadOfRamp || !withinLength) return Math.abs(d.y) + 500

  return Math.abs(d.y) - halfWidth - halfSpanFt
}

/** True if this point is clear of the landing area by at least `marginFt`. */
export function isClearOfLandingArea(xFt, yFt, halfSpanFt = 0, marginFt = 0) {
  return clearanceFt(xFt, yFt, halfSpanFt) >= marginFt
}

/**
 * The footprint of a parked Tomcat, feet.
 *
 * WINGS SPREAD, not swept, and that is not the choice it should be. Aircraft
 * are parked with the wings back — 68 degrees takes the span from 64 ft to
 * 38 ft, and on a deck where every foot is spoken for that is the difference
 * between nine aeroplanes and six. But the model this trainer parks is
 * authored wings-forward and nothing sweeps the clones, so checking against a
 * swept footprint would be checking a shape that is not on the deck. Half the
 * spread span it is, until the model can sweep.
 *
 * The fuselage matters too: a Tomcat is 19.1 m long and parked at an angle, so
 * a point-and-circle test using only the span understates the footprint. The
 * radius below is the larger of the two half-extents, which is conservative
 * for every heading.
 */
export const PARKED_HALF_SPAN_FT = 19.55 / 2 / 0.3048

/** Half the length, for the same reason. */
export const PARKED_HALF_LENGTH_FT = 19.1 / 2 / 0.3048

/** A circle that contains the aeroplane whichever way it is pointing. */
export const PARKED_RADIUS_FT = Math.max(PARKED_HALF_SPAN_FT, PARKED_HALF_LENGTH_FT)

/**
 * How much deck a parked aeroplane actually needs under it.
 *
 * NOT its full 32 ft radius. Carriers park aircraft with the wings and the
 * tail hanging out over the water — that is the entire technique, and it is
 * why a deck 250 ft wide can hold sixty aeroplanes. What must be on steel is
 * the undercarriage: a Tomcat's main gear track is about 16 ft, so twelve feet
 * of margin round the spot covers the wheels and the tyre that has to be
 * chocked next to them.
 *
 * Requiring the whole wingspan to be aboard rejects every real parking spot on
 * the ship, which is how this was first written.
 */
export const PARKED_WHEELS_MARGIN_FT = 12

/**
 * Is this a usable parking spot: clear of the landing area AND on the ship?
 *
 * Both halves are needed and only the first was originally being asked. Six of
 * the nine spots passed the foul-line check while hanging over open water.
 */
export function isUsableSpot(spot, marginFt = 8) {
  return (
    isClearOfLandingArea(spot.x, spot.y, PARKED_RADIUS_FT, marginFt) &&
    isOnFlightDeck(spot.x, spot.y, PARKED_WHEELS_MARGIN_FT)
  )
}
